import { AController } from "@eagerlogic/react-mvc";
import { ApiException } from "rpc/rpc";
import { TeamManagementRpc } from "rpc/settings/TeamManagementRpc";
import {
    TeamModel,
    TeamItemModel,
    UserItemModel,
    CreateTeamDialogModel,
    InviteUserDialogModel,
    EditUserDialogModel,
    ChangeVacationCountDialogModel
} from "../model/TeamModel";

export class TeamController extends AController<TeamModel> {

    protected createInitialModel(): TeamModel {
        var model = new TeamModel();
        model.isInit = true;
        this.fetchData(true);
        return model;
    }

    // ---------------------------------------------------------------------
    // Data loading
    // ---------------------------------------------------------------------

    private fetchData(initial: boolean) {
        TeamManagementRpc.getData().then((res) => {
            this.model.isInit = false;

            this.model.teams = res.teams.map((t) => new TeamItemModel(t.id, t.name, t.userCount));
            this.model.users = res.users.map((u) => new UserItemModel(
                u.id, u.name, u.email, u.roleName, u.roleTitle,
                u.teamId == null ? null : u.teamId,
                u.teamName == null ? null : u.teamName,
                u.pendingInvitation
            ));

            this.render(this.model);
        });
    }

    // ---------------------------------------------------------------------
    // Teams
    // ---------------------------------------------------------------------

    public onCreateTeamClicked() {
        this.model.createTeamDialogModel = new CreateTeamDialogModel();
        this.render(this.model);
    }

    public onCreateTeamCancelClicked() {
        this.model.createTeamDialogModel = null;
        this.render(this.model);
    }

    public onCreateTeamSaveClicked(name: string) {
        var dm = this.model.createTeamDialogModel;
        dm.name = name;
        dm.nameError = null;

        TeamManagementRpc.createTeam(name).then(() => {
            this.model.createTeamDialogModel = null;
            this.render(this.model);
            this.fetchData(false);
        }).catch((ex: ApiException) => {
            dm.nameError = this.fieldError(ex, "name");
            this.render(this.model);
        });
    }

    public onDeleteTeamClicked(teamId: number) {
        this.model.deletingTeamId = teamId;
        this.render(this.model);
    }

    public onCancelDeleteTeam() {
        this.model.deletingTeamId = null;
        this.render(this.model);
    }

    public onSureDeleteTeam() {
        var teamId = this.model.deletingTeamId;
        this.model.deletingTeamId = null;
        this.render(this.model);

        TeamManagementRpc.deleteTeam(teamId).then(() => {
            this.fetchData(false);
        });
    }

    // ---------------------------------------------------------------------
    // Invitations
    // ---------------------------------------------------------------------

    public onInviteUserClicked() {
        this.model.inviteUserDialogModel = new InviteUserDialogModel();
        this.render(this.model);
    }

    public onInviteUserCancelClicked() {
        this.model.inviteUserDialogModel = null;
        this.render(this.model);
    }

    public onInviteUserSaveClicked(dm: InviteUserDialogModel) {
        this.clearInviteErrors(dm);
        this.model.inviteUserDialogModel = dm;
        this.render(this.model);

        TeamManagementRpc.inviteUser({
            name: dm.name,
            email: dm.email,
            role: dm.role,
            teamId: dm.teamId,
            yearlyVacations: dm.yearlyVacations,
            rolledVacations: dm.rolledVacations,
            usedUpVacations: dm.usedUpVacations
        }).then(() => {
            this.model.inviteUserDialogModel = null;
            this.render(this.model);
            this.fetchData(false);
        }).catch((ex: ApiException) => {
            dm.nameError = this.fieldError(ex, "name");
            dm.emailError = this.fieldError(ex, "email");
            dm.roleError = this.fieldError(ex, "role");
            dm.teamError = this.fieldError(ex, "team");
            dm.yearlyVacationsError = this.fieldError(ex, "yearlyVacations");
            dm.rolledVacationsError = this.fieldError(ex, "rolledVacations");
            dm.usedUpVacationsError = this.fieldError(ex, "usedUpVacations");
            this.render(this.model);
        });
    }

    public onResendInvitationClicked(invitationId: string) {
        TeamManagementRpc.resendInvitation(invitationId);
    }

    public onRevokeInvitationClicked(user: UserItemModel) {
        this.model.deletingUser = user;
        this.render(this.model);
    }

    // ---------------------------------------------------------------------
    // Users
    // ---------------------------------------------------------------------

    public onEditUserClicked(user: UserItemModel) {
        this.model.editUserDialogModel = new EditUserDialogModel(
            parseInt(user.id, 10),
            user.name,
            user.roleName,
            user.teamId == null ? "" : "" + user.teamId
        );
        this.render(this.model);
    }

    public onEditUserCancelClicked() {
        this.model.editUserDialogModel = null;
        this.render(this.model);
    }

    public onEditUserSaveClicked(dm: EditUserDialogModel) {
        dm.nameError = null;
        dm.roleError = null;
        dm.teamError = null;
        this.model.editUserDialogModel = dm;
        this.render(this.model);

        TeamManagementRpc.editUser({
            userId: dm.userId,
            name: dm.name,
            role: dm.role,
            teamId: dm.teamId
        }).then(() => {
            this.model.editUserDialogModel = null;
            this.render(this.model);
            this.fetchData(false);
        }).catch((ex: ApiException) => {
            dm.nameError = this.fieldError(ex, "name");
            dm.roleError = this.fieldError(ex, "role");
            dm.teamError = this.fieldError(ex, "team");
            this.render(this.model);
        });
    }

    public onDeleteUserClicked(user: UserItemModel) {
        this.model.deletingUser = user;
        this.render(this.model);
    }

    public onCancelDeleteUser() {
        this.model.deletingUser = null;
        this.render(this.model);
    }

    public onSureDeleteUser() {
        var user = this.model.deletingUser;
        this.model.deletingUser = null;
        this.render(this.model);

        if (user.pendingInvitation) {
            TeamManagementRpc.revokeInvitation(user.id).then(() => {
                this.fetchData(false);
            });
        } else {
            TeamManagementRpc.deleteUser(parseInt(user.id, 10)).then(() => {
                this.fetchData(false);
            });
        }
    }

    // ---------------------------------------------------------------------
    // Vacation count
    // ---------------------------------------------------------------------

    public onChangeVacationCountClicked(user: UserItemModel) {
        this.model.changeVacationCountDialogModel = new ChangeVacationCountDialogModel(
            parseInt(user.id, 10), "0", "0"
        );
        this.render(this.model);
    }

    public onChangeVacationCountCancelClicked() {
        this.model.changeVacationCountDialogModel = null;
        this.render(this.model);
    }

    public onChangeVacationCountSaveClicked(dm: ChangeVacationCountDialogModel) {
        dm.yearlyVacationCountError = null;
        dm.rolledVacationCountError = null;
        this.model.changeVacationCountDialogModel = dm;
        this.render(this.model);

        TeamManagementRpc.changeVacationCount({
            userId: dm.userId,
            yearlyVacationCount: dm.yearlyVacationCount,
            rolledVacationCount: dm.rolledVacationCount
        }).then(() => {
            this.model.changeVacationCountDialogModel = null;
            this.render(this.model);
        }).catch((ex: ApiException) => {
            dm.yearlyVacationCountError = this.fieldError(ex, "yearlyVacationCount");
            dm.rolledVacationCountError = this.fieldError(ex, "rolledVacationCount");
            this.render(this.model);
        });
    }

    // ---------------------------------------------------------------------
    // Helpers
    // ---------------------------------------------------------------------

    private clearInviteErrors(dm: InviteUserDialogModel) {
        dm.nameError = null;
        dm.emailError = null;
        dm.roleError = null;
        dm.teamError = null;
        dm.yearlyVacationsError = null;
        dm.rolledVacationsError = null;
        dm.usedUpVacationsError = null;
    }

    private fieldError(ex: ApiException, field: string): string {
        if (ex != null && ex.fieldErrors != null && ex.fieldErrors[field] != null) {
            return ex.fieldErrors[field];
        }
        return null;
    }

}
